import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import "./Sidebar.css";
import { CiLogout } from "react-icons/ci";
import {
  MdDashboard,
  MdFolder,
  MdPeople,
  MdShowChart,
  MdBlock,
  MdDescription,
  MdMonitor,
  MdSettings,
  MdMenu,
  MdClose,
  MdSecurity,
  MdAutoFixHigh,
  MdCompareArrows,
} from "react-icons/md";

const Sidebar = () => {
  const location = useLocation();
  const { user, permissions } = useSelector((state) => state.auth);
  const isActive = (path) => location.pathname === path;
  const [isOpen, setIsOpen] = useState(false);

  // Close sidebar when route changes (mobile)
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  // Close sidebar when clicking outside (handled by overlay)
  const handleOverlayClick = () => {
    setIsOpen(false);
  };

  return (
    <>
      {/* Mobile Navbar */}
      <div className="mobile-navbar">
        <div className="mobile-navbar-brand">
          <img src="/favicon.avif" alt="UrbanPillar" />
          <span>Microsite Cloud</span>
        </div>
        <button
          className="mobile-menu-toggle"
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Toggle menu"
        >
          {isOpen ? <MdClose size={24} /> : <MdMenu size={24} />}
        </button>
      </div>

      {/* Overlay for mobile */}
      <div
        className={`sidebar-overlay ${isOpen ? "active" : ""}`}
        onClick={handleOverlayClick}
      />

      <div className={`sidebar ${isOpen ? "open" : ""}`}>
        <div className="sidebar-brand">
          <img
            src="/logo.avif"
            alt="UrbanPillar"
            className="brand-logo-img rounded-1"
          />
          <span className="brand-subtitle">Microsite Cloud</span>
        </div>

        <div className="sidebar-menu">
          {/* MAIN SECTION */}
          <div className="sidebar-divider">MAIN</div>
          {(!permissions || permissions.Dashboard !== false) && (
            <Link
              to="/"
              className={`menu-item ${isActive("/") ? "active" : ""}`}
            >
              <MdDashboard size={20} />
              <span>Dashboard</span>
            </Link>
          )}

          {/* OPERATIONS SECTION */}
          <div className="sidebar-divider">OPERATIONS</div>
          {(!permissions ||
            permissions.Projects !== false ||
            permissions.ProjectsViewOnly === true) && (
            <Link
              to="/projects"
              className={`menu-item ${isActive("/projects") ? "active" : ""}`}
            >
              <MdFolder size={20} />
              <span>Projects</span>
            </Link>
          )}

          {(!permissions || permissions.Leads !== false) && (
            <Link
              to="/leads"
              className={`menu-item ${isActive("/leads") ? "active" : ""}`}
            >
              <MdPeople size={20} />
              <span>Leads</span>
            </Link>
          )}

          {(!permissions || permissions.LiveLeads !== false) && (
            <Link
              to="/live-leads"
              className={`menu-item ${isActive("/live-leads") ? "active" : ""}`}
            >
              <MdShowChart size={20} />
              <span>Live Leads</span>
            </Link>
          )}

          {/* TOOLS SECTION */}
          <div className="sidebar-divider">TOOLS</div>
          {(!permissions || permissions.Analysis !== false) && (
            <Link
              to="/analysis"
              className={`menu-item ${isActive("/analysis") ? "active" : ""}`}
            >
              <MdShowChart size={20} />
              <span>Analysis</span>
            </Link>
          )}

          {(!permissions || permissions.SEOAnalyzer !== false) && (
            <Link
              to="/seo"
              className={`menu-item ${isActive("/seo") ? "active" : ""}`}
            >
              <MdAutoFixHigh size={20} />
              <span>SEO Analyzer</span>
            </Link>
          )}

          {(!permissions || permissions.Tools !== false) && (
            <Link
              to="/tools"
              className={`menu-item ${isActive("/tools") ? "active" : ""}`}
            >
              <MdCompareArrows size={20} />
              <span>Utilities</span>
            </Link>
          )}

          {(!permissions || permissions.Monitor !== false) && (
            <Link
              to="/monitor"
              className={`menu-item ${isActive("/monitor") ? "active" : ""}`}
            >
              <MdMonitor size={20} />
              <span>Monitor</span>
            </Link>
          )}

          {(!permissions || permissions.SystemLogs !== false) && (
            <Link
              to="/system-logs"
              className={`menu-item ${isActive("/system-logs") ? "active" : ""}`}
            >
              <MdDescription size={20} />
              <span>System Logs</span>
            </Link>
          )}

          {/* MANAGEMENT SECTION */}
          <div className="sidebar-divider">MANAGEMENT</div>
          {(!permissions || permissions.VisitorLogs !== false) && (
            <Link
              to="/visit-logs"
              className={`menu-item ${isActive("/visit-logs") ? "active" : ""}`}
            >
              <MdDescription size={20} />
              <span>Visitor Logs</span>
            </Link>
          )}

          {(!permissions || permissions.BlockedIPs !== false) && (
            <Link
              to="/blocked-ips"
              className={`menu-item ${isActive("/blocked-ips") ? "active" : ""}`}
            >
              <MdBlock size={20} />
              <span>Blocked IPs</span>
            </Link>
          )}

          {(!permissions || permissions.Settings !== false) && (
            <Link
              to="/settings"
              className={`menu-item ${isActive("/settings") ? "active" : ""}`}
            >
              <MdSettings size={20} />
              <span>Settings</span>
            </Link>
          )}

          {(!permissions || permissions.RoleAccess !== false) && (
            <>
              <div className="sidebar-divider">SECURITY</div>
              <Link
                to="/role-access"
                className={`menu-item ${isActive("/role-access") ? "active" : ""}`}
              >
                <MdSecurity size={20} />
                <span>Role Access</span>
              </Link>
            </>
          )}
        </div>

        <div className="sidebar-footer">
          <div className="user-profile">
            <div className="user-avatar">
              <img
                src={`https://ui-avatars.com/api/?name=${user?.name || user?.email || "User"}&background=random`}
                alt={user?.name || user?.email || "User"}
              />
            </div>
            <div className="user-info">
              <p className="user-name">
                {user?.name || user?.email?.split("@")[0] || "Guest User"}
              </p>
              <p className="user-role">{user?.role || "User"}</p>
            </div>
            <Link to="/logout" className="logout-button" title="Sign Out">
              <CiLogout size={22} />
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
